import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiMaximize2, FiX, FiInfo, FiExternalLink } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

const Schedules = () => {
    const [institutions, setInstitutions] = useState([]);
    const [selectedInst, setSelectedInst] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSchedules();
    }, []);

    const fetchSchedules = async () => {
        try {
            const data = await fetchAPI('/get_schedules.php');
            setInstitutions(data);
        } catch (error) {
            console.error('Error fetching schedules:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="py-24 px-6 max-w-[90rem] mx-auto bg-[#0a0a0a] relative overflow-hidden" id="schedules">
            {/* Background Aesthetic Elements */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent:80%)]"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>

            <div className="flex flex-col lg:flex-row justify-between items-end mb-16 gap-8 relative z-10">
                <div className="max-w-2xl">
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-4 mb-4"
                    >
                        <div className="h-[2px] w-8 bg-blue-500"></div>
                        <span className="text-blue-500 font-black uppercase tracking-[0.3em] text-[10px]">Academic Registry // 2026</span>
                    </motion.div>
                    <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white leading-tight uppercase italic">
                        Class <span className="text-blue-500">Schedules</span>
                    </h2>
                </div>
                <div className="text-right hidden lg:block">
                    <div className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em] mb-1">System Status</div>
                    <div className="flex items-center gap-2 text-blue-500 font-black text-xs uppercase tracking-widest">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                        Registry Live
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20 relative z-10">
                    <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 relative z-10">
                    {institutions.map((inst, i) => (
                        <motion.div
                            key={inst.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                            viewport={{ once: true }}
                            className="bg-white/5 backdrop-blur-3xl rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-6 flex flex-col items-center text-center border border-white/10 shadow-2xl hover:border-blue-500/40 transition-all duration-500 group relative overflow-hidden h-full"
                        >
                            {/* Decorative Glow */}
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-600/10 rounded-full blur-3xl group-hover:bg-blue-600/20 transition-all"></div>

                            {/* HUD Top Detail */}
                            <div className="w-full flex justify-between items-center mb-4 md:mb-6 px-1">
                                <div className="h-[1px] w-4 bg-white/10 group-hover:bg-blue-500/50 transition-colors"></div>
                                <div className="h-[1px] w-4 bg-white/10 group-hover:bg-blue-500/50 transition-colors"></div>
                            </div>

                            {/* Logo Container */}
                            <div className="w-16 h-16 md:w-24 md:h-24 bg-white/5 rounded-2xl mb-4 md:mb-6 flex items-center justify-center p-3 md:p-4 border border-white/10 transition-all duration-500 group-hover:scale-105 group-hover:bg-white/10 group-hover:border-blue-500/30 group-hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] relative overflow-hidden">
                                {inst.logo_url ? (
                                    <img
                                        src={inst.logo_url}
                                        alt={inst.name}
                                        className="max-w-full max-h-full object-contain filter drop-shadow-md brightness-110 relative z-10"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-600/20 to-blue-800/20 text-blue-500 font-black text-xl md:text-3xl uppercase italic ${inst.logo_url ? 'hidden' : 'flex'}`}
                                >
                                    {inst.name.charAt(0)}
                                </div>
                            </div>

                            <h3 className="text-sm md:text-xl font-black text-white tracking-tighter leading-none mb-1 uppercase italic group-hover:text-blue-400 transition-colors">{inst.name}</h3>
                            <p className="text-[8px] md:text-[9px] font-black text-blue-500/80 mb-3 md:mb-4 uppercase tracking-[0.2em]">{inst.location}</p>

                            <p className="hidden md:block text-[10px] text-gray-500 font-medium mb-8 leading-relaxed line-clamp-2 px-2">
                                {inst.description || 'Academic registry data for 2026 intake.'}
                            </p>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setSelectedInst(inst)}
                                className="mt-auto flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 bg-blue-600 text-white rounded-xl font-black text-[8px] md:text-[9px] uppercase tracking-[0.2em] shadow-xl shadow-blue-600/20 hover:bg-blue-700 transition-all w-full justify-center group/btn"
                            >
                                Open <span className="hidden md:inline">Portal</span> <FiChevronRight className="transition-transform group-hover/btn:translate-x-1" />
                            </motion.button>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Modal for displaying the timetable */}
            <AnimatePresence>
                {selectedInst && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedInst(null)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
                        ></motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            className="relative z-10 max-w-2xl w-full bg-white rounded-[1.5rem] overflow-hidden shadow-[0_20px_70px_rgba(0,0,0,0.3)] flex flex-col border border-white/10"
                            style={{ maxHeight: '90vh' }}
                        >
                            {/* Header */}
                            <div className="p-6 md:p-8 bg-blue-600 text-white flex justify-between items-center relative">
                                <div className="relative z-10">
                                    <h3 className="text-xl md:text-2xl font-black tracking-tight uppercase leading-none mb-1">
                                        {selectedInst.name} TIMETABLE
                                    </h3>
                                    <p className="text-blue-100/80 font-medium text-sm italic">{selectedInst.location}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedInst(null)}
                                    className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all z-10 group"
                                >
                                    <FiX size={24} className="group-hover:rotate-90 transition-transform" />
                                </button>
                            </div>

                            {/* Content Section */}
                            <div className="p-4 md:p-6 overflow-y-auto bg-[#f8faff]">
                                {/* Desktop Table View */}
                                <div className="hidden md:block bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-blue-50/50 border-b border-blue-100">
                                                <th className="px-6 py-4 text-[11px] font-black text-blue-600 uppercase tracking-wider">Session</th>
                                                <th className="px-6 py-4 text-[11px] font-black text-blue-600 uppercase tracking-wider">Class Segment</th>
                                                <th className="px-6 py-4 text-[11px] font-black text-blue-600 uppercase tracking-wider">Window</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/[0.03]">
                                            {selectedInst.schedules && selectedInst.schedules.length > 0 ? (
                                                selectedInst.schedules.map((schedule, idx) => (
                                                    <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                                                        <td className="px-6 py-4 text-[11px] font-bold text-gray-400">
                                                            #{String(idx + 1).padStart(3, '0')}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-black text-gray-800 uppercase tracking-tight">
                                                                    {schedule.class_name}
                                                                </span>
                                                                <span className="text-[10px] font-bold text-blue-500/70 uppercase">
                                                                    {schedule.day}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="text-xs font-bold text-gray-600">
                                                                {schedule.time_slot}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="px-6 py-12 text-center text-gray-400 font-bold text-xs uppercase italic">
                                                        No session data found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="md:hidden space-y-3">
                                    {selectedInst.schedules && selectedInst.schedules.length > 0 ? (
                                        selectedInst.schedules.map((schedule, idx) => (
                                            <div key={idx} className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                                        {schedule.class_name}
                                                    </div>
                                                    <span className="text-[11px] font-black text-gray-800 uppercase">
                                                        {schedule.day}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                                    <span className="text-sm font-bold text-gray-600 tracking-tight">
                                                        {schedule.time_slot}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="bg-white border border-blue-100 rounded-2xl p-10 text-center">
                                            <p className="text-gray-400 font-bold text-xs uppercase italic">
                                                No session data found.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 bg-white border-t border-blue-50 flex justify-center items-center">
                                <button
                                    onClick={() => setSelectedInst(null)}
                                    className="w-full py-4 bg-[#f3f6f9] text-gray-600 rounded-2xl font-black text-sm uppercase tracking-tight hover:bg-gray-100 transition-all italic"
                                >
                                    Close Portal
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Support Banner */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="mt-20 p-12 bg-white/[0.02] border border-white/5 rounded-[4rem] text-center space-y-8 relative overflow-hidden group shadow-2xl"
            >
                <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-white/10 rounded-tl-[4rem] group-hover:border-blue-500/30 transition-colors"></div>
                <div className="relative z-10 space-y-4">
                    <h3 className="text-2xl md:text-5xl font-black text-white tracking-tighter uppercase italic">Registry <span className="text-blue-500">Inquiry?</span></h3>
                    <p className="text-white/40 font-medium max-w-xl mx-auto text-[10px] uppercase tracking-[0.3em] leading-relaxed">Direct academic support available 24/7 via the 2026 registry helpline. Precision enrollment assistance protocols active.</p>
                </div>
                <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href="tel:+94714336294"
                    className="inline-block px-12 py-6 bg-blue-600 text-white rounded-[2rem] font-black text-xs tracking-widest shadow-2xl shadow-blue-600/30 hover:bg-blue-700 transition-all relative z-10 uppercase italic"
                >
                    Connect with Registry Support
                </motion.a>
            </motion.div>
        </section>
    );
};

export default Schedules;
